/**
=========================================================
* Soft UI Dashboard PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// Core
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Components
import CustomCell from "pages/ManageContracts/Single/ContractCard/CustomCell";
import FormulaBlock from "pages/ManageContracts/Single/ContractCard/FormulaBlock";
import { Card, Divider, Grid, Typography } from "@mui/material";

const kTypes = ["k1", "k2", "k5", "k6"];
const periods = [
  { field: "peak_value", accessor: "ponta", labelKey: "single.koefficients.table.columns.ponta" },
  {
    field: "mid_peak_value",
    accessor: "cheias",
    labelKey: "single.koefficients.table.columns.cheias",
  },
  {
    field: "off_peak_value",
    accessor: "vazio",
    labelKey: "single.koefficients.table.columns.vazio",
  },
  {
    field: "super_off_peak_value",
    accessor: "super_vazio",
    labelKey: "single.koefficients.table.columns.super_vazio",
  },
];

const koefficientUnit = (kType) => (kType !== "k6" ? "€/kWh" : "%");

function Koefficients({ data, action, onChange }) {
  const { t } = useTranslation(["contracts"]);
  const disabled = action === "view";

  return (
    <Card
      sx={{
        p: 2,
        border: (theme) =>
          theme.palette.mode === "dark" ? "1px solid rgba(255,255,255,0.12)" : "none",
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : undefined,
      }}
    >
      <Grid
        container
        spacing={1}
        alignItems="center"
        sx={{ mb: 0.5, display: { xs: "none", sm: "flex" } }}
      >
        <Grid item sm={2}>
          <Typography variant="caption" fontWeight="bold">
            {t("single.koefficients.table.columns.type_k")}
          </Typography>
        </Grid>
        {periods.map(({ labelKey }) => (
          <Grid item sm={2.5} key={labelKey}>
            <Typography variant="caption" fontWeight="bold">
              {t(labelKey)}
            </Typography>
          </Grid>
        ))}
      </Grid>
      <Divider />
      {kTypes.map((kType) => (
        <Grid container spacing={1} alignItems="center" key={kType} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={2}>
            <Typography variant="button" fontWeight="bold">
              {kType.toUpperCase()}
            </Typography>
            <Typography variant="caption" sx={{ ml: 0.5 }}>
              ({koefficientUnit(kType)})
            </Typography>
          </Grid>
          {periods.map(({ field, labelKey }) => {
            const fieldKey = `${kType}_${field}`;
            const defaultVal = kType === "k6" ? 100 : 0;
            return (
              <Grid item xs={6} sm={2.5} key={field}>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  sx={{ display: { xs: "block", sm: "none" }, mb: 0.5 }}
                >
                  {t(labelKey)}
                </Typography>
                <CustomCell
                  value={data?.[fieldKey] ?? defaultVal}
                  unit={koefficientUnit(kType)}
                  disabled={disabled}
                  onChange={(e) => onChange(fieldKey, e.target.value)}
                  name={fieldKey}
                  reference={fieldKey}
                />
              </Grid>
            );
          })}
        </Grid>
      ))}
      <FormulaBlock
        title={t("single.koefficients.formula.title")}
        body={`${t("single.koefficients.formula.body_buy")}\n${t(
          "single.koefficients.formula.body_sell"
        )}`}
      />
    </Card>
  );
}

Koefficients.defaultProps = {
  data: {},
  onChange: null,
};

Koefficients.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object,
  action: PropTypes.oneOf(["view", "edit", "new"]).isRequired,
  onChange: PropTypes.func,
};

export default Koefficients;
